/**
 * Objectiv Cinematic Film Grain Overlay Engine
 * High-performance procedural HTML5 2D Canvas noise generator.
 *
 * Performance features:
 * - Eight pre-generated 64x64 noise patterns cycled across the viewport
 * - 32-bit integer pixel buffer writes (1 memory write per pixel instead of 4)
 * - No per-frame noise generation, pixel uploads or pattern allocations
 * - Throttled 10 FPS animation loop (100ms per frame)
 * - Automatic sleep when document is hidden (background tab)
 * - Native prefers-reduced-motion accessibility support
 */
(() => {
  'use strict';

  const initGrain = () => {
    const grainCanvas = document.querySelector('[data-site-grain]');
    if (!grainCanvas) return;

    const grainContext = grainCanvas.getContext('2d', { alpha: true });
    if (!grainContext) return;

    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const TILE_SIZE = 64;
    const TOTAL_PIXELS = TILE_SIZE * TILE_SIZE;
    const PATTERN_COUNT = 8;
    const FRAME_INTERVAL = 1000 / 10; // 10 FPS

    // Determine endianness once for ultra-fast 32-bit word writes
    let isLittleEndian = true;
    try {
      const probeBuffer = new ArrayBuffer(4);
      const probe8 = new Uint8Array(probeBuffer);
      const probe32 = new Uint32Array(probeBuffer);
      probe32[0] = 0x08ffffff;
      isLittleEndian = probe8[0] === 255 && probe8[3] === 8;
    } catch (_) {
      isLittleEndian = false;
    }

    // Offscreen 64x64 scratchpad tile
    const tileCanvas = document.createElement('canvas');
    tileCanvas.width = TILE_SIZE;
    tileCanvas.height = TILE_SIZE;
    const tileContext = tileCanvas.getContext('2d', { alpha: true });
    if (!tileContext) return;

    // Single pre-allocated ImageData buffer (prevents garbage collection churn)
    const grainImageData = tileContext.createImageData(TILE_SIZE, TILE_SIZE);
    const grainData8 = grainImageData.data;
    const grainData32 = new Uint32Array(grainData8.buffer);

    let timerId = null;
    let pattern = null;
    // Mobile document pages extend the canvas behind the status bar, so its
    // origin can sit above the viewport.
    let canvasLeft = 0;
    let canvasTop = 0;
    const menuOpen = () => document.documentElement.classList.contains('obj-menu-open');

    const paintGrain = () => {
      if (document.hidden || menuOpen() || !pattern) return;
      const alpha = document.documentElement.dataset.colorMode === 'dark' ? 5 : 8;
      grainContext.clearRect(0, 0, grainCanvas.width, grainCanvas.height);
      grainContext.fillStyle = pattern;
      grainContext.globalAlpha = alpha / 255;
      grainContext.fillRect(0, 0, grainCanvas.width, grainCanvas.height);
      const video = document.querySelector('[data-about-video-hero] video');
      if (video) {
        const bounds = video.getBoundingClientRect();
        const x = Math.max(0, bounds.left - canvasLeft), y = Math.max(0, bounds.top - canvasTop);
        const width = Math.min(grainCanvas.width, bounds.right - canvasLeft) - x;
        const height = Math.min(grainCanvas.height, bounds.bottom - canvasTop) - y;
        if (width > 0 && height > 0) {
          grainContext.clearRect(x, y, width, height);
          grainContext.globalAlpha = 0.06;
          grainContext.fillRect(x, y, width, height);
        }
      }
      grainContext.globalAlpha = 1;
    };

    // Match the canvas buffer to its CSS box (the viewport, or the full screen
    // on mobile document pages) so the grain stays at one buffer pixel per CSS pixel.
    const resizeCanvas = () => {
      const bounds = grainCanvas.getBoundingClientRect();
      canvasLeft = bounds.left;
      canvasTop = bounds.top;
      const w = Math.max(1, Math.ceil(bounds.width || window.innerWidth));
      const h = Math.max(1, Math.ceil(bounds.height || window.innerHeight));
      if (grainCanvas.width !== w || grainCanvas.height !== h) {
        grainCanvas.width = w;
        grainCanvas.height = h;
        grainContext.imageSmoothingEnabled = true;
      }
    };

    // CanvasPattern snapshots the tile, so one scratchpad can build the cache.
    const createNoisePattern = () => {
      const alpha = 255;
      const white = (alpha << 24) | 0x00ffffff;
      const black = alpha << 24;

      if (isLittleEndian) {
        for (let i = 0; i < TOTAL_PIXELS; i++) {
          grainData32[i] = Math.random() > 0.5 ? white : black;
        }
      } else {
        const len = grainData8.length;
        for (let i = 0; i < len; i += 4) {
          const val = Math.random() > 0.5 ? 255 : 0;
          grainData8[i] = val;
          grainData8[i + 1] = val;
          grainData8[i + 2] = val;
          grainData8[i + 3] = alpha;
        }
      }

      tileContext.putImageData(grainImageData, 0, 0);

      return grainContext.createPattern(tileCanvas, 'repeat');
    };
    const patterns = Array.from({ length: PATTERN_COUNT }, createNoisePattern);
    if (patterns.some(value => !value)) return;
    let patternIndex = 0;

    const renderGrain = () => {
      if (document.hidden || menuOpen()) return;
      pattern = patterns[patternIndex];
      patternIndex = (patternIndex + 1) % patterns.length;
      paintGrain();
    };

    // Schedule only the ten paints per second; do not wake every display frame.
    const loop = () => {
      timerId = null;
      if (document.hidden || menuOpen() || reducedMotionQuery.matches) return;
      renderGrain();
      timerId = window.setTimeout(loop, FRAME_INTERVAL);
    };

    const startAnimation = () => {
      if (menuOpen()) return;
      if (reducedMotionQuery.matches) {
        renderGrain();
        return;
      }
      if (timerId === null && !document.hidden) {
        timerId = window.setTimeout(loop, FRAME_INTERVAL);
      }
    };

    const stopAnimation = () => {
      window.clearTimeout(timerId);
      timerId = null;
    };

    document.addEventListener('objectiv:menu-change', ({ detail }) => {
      if (detail.open) stopAnimation();
      else {
        renderGrain();
        startAnimation();
      }
    });

    // Initialize layout and initial render
    resizeCanvas();
    renderGrain();
    startAnimation();

    // Also update static reduced-motion grain immediately when the mode changes.
    document.addEventListener('objectiv:color-mode-change', renderGrain);
    window.addEventListener('scroll', () => {
      if (reducedMotionQuery.matches && document.querySelector('[data-about-video-hero]')) paintGrain();
    }, { passive: true });

    // Resize listener (passive)
    window.addEventListener(
      'resize',
      () => {
        resizeCanvas();
        renderGrain();
      },
      { passive: true }
    );

    // Sleep when tab is inactive, wake immediately when focused
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopAnimation();
      } else {
        resizeCanvas();
        renderGrain();
        startAnimation();
      }
    });

    // Support dynamic OS motion preference toggling
    if (typeof reducedMotionQuery.addEventListener === 'function') {
      reducedMotionQuery.addEventListener('change', (e) => {
        if (e.matches) {
          stopAnimation();
          renderGrain();
        } else {
          startAnimation();
        }
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGrain, { once: true });
  } else {
    initGrain();
  }
})();
